import { useEffect, useState } from "react";
import axios from "axios";
import { Activity } from "lucide-react";
import { DailyChart } from "../pmax/PmaxDaily";
import { toWeeks } from "./weeklyBuckets";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "Oct 01" + summary period -> "2026-10-01" (handles reports that cross a year end)
const toIso = (label, start) => {
  const m = /([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2})/.exec(label || "");
  if (!m) return /^\d{4}-\d{2}-\d{2}$/.test(label || "") ? label : null;
  const mi = MONTHS.findIndex((x) => x.toLowerCase() === m[1].toLowerCase());
  if (mi < 0 || !start) return null;
  const sy = parseInt(start.slice(0, 4), 10);
  const sm = parseInt(start.slice(5, 7), 10) - 1;
  const year = mi < sm ? sy + 1 : sy;
  return `${year}-${String(mi + 1).padStart(2, "0")}-${String(m[2]).padStart(2, "0")}`;
};

// Newest upload wins when two summaries cover the same day. CTV clicks are never counted: nobody clicks a TV.
export const joinSummaryDaily = (summaries = []) => {
  const byDay = new Map();
  [...summaries].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")).forEach((s) => {
    const chans = s.channels || {};
    const tc = Object.values(chans).reduce((t, x) => t + (x.clicks || 0), 0);
    const clkShare = tc && chans.ctv?.clicks ? (tc - chans.ctv.clicks) / tc : 1;
    for (const r of s.daily || []) {
      const iso = toIso(r.date, s.report_start);
      if (iso) byDay.set(iso, { iso, impressions: Math.round(Number(r.impressions) || 0), clicks: Math.round((Number(r.clicks) || 0) * clkShare * 10) / 10 });
    }
  });
  return [...byDay.values()].sort((a, b) => a.iso.localeCompare(b.iso));
};

export const CampaignSummaryDaily = ({ API, clientId, reportStart, reportEnd }) => {
  const [summaries, setSummaries] = useState([]);
  useEffect(() => {
    if (!API || !clientId) return;
    axios.get(`${API}/campaign-summaries`, { params: { client_id: clientId } })
      .then((r) => setSummaries((r.data || []).filter((s) => (s.daily || []).length)))
      .catch(() => setSummaries([]));
  }, [API, clientId]);
  const inPeriod = joinSummaryDaily(summaries).filter((r) => (!reportStart || r.iso >= reportStart) && (!reportEnd || r.iso <= reportEnd));
  if (inPeriod.length < 3) return null;
  const weeks = toWeeks(inPeriod, reportStart, reportEnd);
  return (
    <div className="bg-gradient-to-br from-emerald-500/10 via-teal-500/[0.06] to-sky-500/[0.08] rounded-2xl p-4 md:p-6 border border-emerald-400/25 mb-6" data-testid="campaign-summary-daily">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="w-4 h-4 text-emerald-300" />
        <p className="text-white font-semibold">Week by week</p>
      </div>
      <p className="text-slate-400 text-xs mb-4">
        Every paid channel together, Monday to Sunday, from your Campaign Summary reports. Clicks leave out CTV, since nobody clicks a TV.
      </p>
      {weeks.length >= 2 ? <DailyChart daily={weeks} capSpikes unit="week" /> : <p className="text-slate-400 text-sm py-8 text-center" data-testid="summary-daily-empty">Not enough data in this period.</p>}
    </div>
  );
};

export default CampaignSummaryDaily;
